import { getEvmClient } from '@1delta/providers'
import type { Address } from 'viem'
import { YieldFetcher } from '../../types'
import { annualizeRateDeltaPercent } from '../annualizeRate'

/**
 * StakedCelo (stCELO) — intrinsic APR from the on-chain exchange rate.
 *
 * stCELO has no public APY API and is value-accruing: a unit of stCELO
 * redeems for more CELO over time as epoch rewards accrue. So the APR
 * is recovered from the *growth* of `Manager.toCelo(1e18)` over a fixed
 * lookback window, annualised:
 *
 *   apr = (rateNow / rateThen - 1) * (yearSeconds / elapsedSeconds)
 *
 * **Requires an archival Celo RPC.** The historical leg is a
 * `toCelo(1e18)` `eth_call` at a ~7-day-old block; the read is only
 * valid on an archive node. The bundled public RPCs (forno, drpc,
 * ankr, …) are pruned and return `0` / error for old state — this
 * fetcher detects that (rates must be ≥ 1e18 and non-decreasing),
 * rotates through the configured RPCs, and throws if none serve archive
 * state. `safeFetch` then logs and leaves stCELO's `supplyRate` at 0
 * rather than emitting a bogus number. Configure an archival Celo RPC
 * (via the providers custom-RPC mechanism) to populate it.
 */

const CHAIN_ID = '42220'
const MANAGER: Address = '0x0239b96d10a434a56cc9e09383077a0490cf9398'

/** 1e18 — stCELO and the rate getter are 18-decimal. */
const ONE_E18 = 10n ** 18n

/** Lookback window for the rate-delta. stCELO rewards step per daily
 *  epoch, so a multi-day window is needed for a stable figure. */
const WINDOW_SECONDS = 7 * 24 * 60 * 60
/** Celo L2 block time is ~1s; used to estimate the historical block.
 *  The actual elapsed time is read back from block timestamps, so a
 *  small drift here only shifts which block is sampled. */
const BLOCK_TIME_SECONDS = 1
const WINDOW_BLOCKS = BigInt(Math.floor(WINDOW_SECONDS / BLOCK_TIME_SECONDS))

/** Number of configured RPCs to try before giving up on archive state. */
const MAX_RPC_TRIES = 5

const TO_CELO_ABI = [
  {
    name: 'toCelo',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'uint256', name: 'stCeloAmount' }],
    outputs: [{ type: 'uint256' }],
  },
] as const

const readRateAt = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: any,
  blockNumber?: bigint,
): Promise<bigint> =>
  client.readContract({
    address: MANAGER,
    abi: TO_CELO_ABI,
    functionName: 'toCelo',
    args: [ONE_E18],
    ...(blockNumber !== undefined ? { blockNumber } : {}),
  }) as Promise<bigint>

const computeApr = async (): Promise<number> => {
  let lastErr: unknown
  for (let rpcId = 0; rpcId < MAX_RPC_TRIES; rpcId++) {
    try {
      const client = getEvmClient(CHAIN_ID, rpcId)
      const head = await client.getBlockNumber()
      const pastBlock = head > WINDOW_BLOCKS ? head - WINDOW_BLOCKS : 0n

      const [rateNow, rateThen, headBlock, thenBlock] = await Promise.all([
        readRateAt(client),
        readRateAt(client, pastBlock),
        client.getBlock({ blockNumber: head }),
        client.getBlock({ blockNumber: pastBlock }),
      ])

      // Archive sanity: both rates must be real (≥ 1e18) and the rate
      // must be non-decreasing. Pruned nodes return 0 here.
      if (rateNow < ONE_E18 || rateThen < ONE_E18 || rateNow < rateThen) {
        throw new Error(
          `non-archival or invalid rates (now=${rateNow}, then=${rateThen})`,
        )
      }

      const elapsed = Number(headBlock.timestamp - thenBlock.timestamp)
      if (elapsed <= 0) throw new Error('non-positive elapsed window')

      const apr = annualizeRateDeltaPercent(rateNow, rateThen, elapsed)
      if (!Number.isFinite(apr) || apr < 0) throw new Error(`bad apr ${apr}`)
      return apr
    } catch (e) {
      lastErr = e
    }
  }
  throw new Error(
    `stCELO APR: no archival Celo RPC returned valid historical state (${String(lastErr)})`,
  )
}

export const stceloFetcher: YieldFetcher = {
  label: 'STCELO',
  fetch: async () => {
    const apr = await computeApr()
    // Key is the canonical token-list assetGroup (not the bare symbol) so
    // the lending-market join `intrinsicYields[asset.assetGroup]` resolves.
    // The LST vault registry reads the same key via its `yieldKey`.
    return { 'Staked CELO::stCELO': apr }
  },
}
